/**
 * USE FIELD VISIT HOOK
 * 
 * Central state management for field visit data
 * Handles loading, saving, and validation
 */

import { useState, useEffect, useCallback } from 'react';
import { DEFAULT_FIELD_VISIT_SETTINGS, getRequiredFieldsForStep } from '../constants/settingsDefaults';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Map backend field visit data to frontend structure
 * Handles both old (capture object) and new (flat) formats
 * Also integrates lead and appointment data for pre-population
 */
const mapBackendToFrontend = (fvData, appointmentId, leadData = null, appointmentData = null) => {
  if (!fvData) return getEmptyFieldVisit(appointmentId);
  
  // If backend already has client_intel at top level, use it with enhancements
  if (fvData.client_intel) {
    const clientIntel = { ...fvData.client_intel };
    
    // Merge in lead data if available and fields are empty
    if (leadData) {
      if (!clientIntel.contact_name) clientIntel.contact_name = leadData.contact_name || '';
      if (!clientIntel.contact_phone) clientIntel.contact_phone = leadData.phone || '';
      if (!clientIntel.contact_email) clientIntel.contact_email = leadData.email || '';
      if (!clientIntel.service_address) clientIntel.service_address = leadData.service_address || '';
      if (!clientIntel.city) clientIntel.city = leadData.city || '';
      if (!clientIntel.state) clientIntel.state = leadData.state || '';
      if (!clientIntel.zip_code) clientIntel.zip_code = leadData.zip_code || '';
      if (!clientIntel.lead_source) clientIntel.lead_source = leadData.lead_source || '';
    }
    
    return {
      ...fvData,
      client_intel: clientIntel,
      current_step: fvData.step_state || fvData.current_step || 1,
    };
  }
  
  // Map old structure (with capture object) to new flat structure
  const capture = fvData.capture || {};
  
  // Build client_intel from lead data first, then overlay any existing capture data
  const clientIntel = {
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    preferred_contact_method: '',
    best_time_to_reach: '',
    decision_makers: { primary: '', secondary: '', notes: '' },
    spouse_involved: '',
    referral_source: '',
    referral_details: '',
    other_quotes: '',
    other_quotes_details: { count: '', competitors: '' },
    property_type: '',
    property_ownership: '',
    property_age: '',
    property_sqft: '',
    hoa_present: '',
    hoa_restrictions: '',
    service_address: '',
    city: '',
    state: '',
    zip_code: '',
    lead_source: '',
    notes: '',
  };
  
  // First populate from lead data
  if (leadData) {
    clientIntel.contact_name = leadData.contact_name || '';
    clientIntel.contact_phone = leadData.phone || '';
    clientIntel.contact_email = leadData.email || '';
    clientIntel.service_address = leadData.service_address || '';
    clientIntel.city = leadData.city || '';
    clientIntel.state = leadData.state || '';
    clientIntel.zip_code = leadData.zip_code || '';
    clientIntel.lead_source = leadData.lead_source || '';
    clientIntel.notes = leadData.description || '';
  }
  // Fallback to appointment data
  else if (appointmentData) {
    clientIntel.contact_name = appointmentData.lead_contact_name || '';
    clientIntel.contact_phone = appointmentData.lead_phone || '';
    clientIntel.service_address = appointmentData.lead_service_address || appointmentData.location_address || '';
  }
  
  // Then overlay any existing capture data
  if (capture.client_discovery) {
    const cd = capture.client_discovery;
    if (cd.contact_name) clientIntel.contact_name = cd.contact_name;
    if (cd.contact_phone) clientIntel.contact_phone = cd.contact_phone;
    if (cd.contact_email) clientIntel.contact_email = cd.contact_email;
    if (cd.preferred_contact_method) clientIntel.preferred_contact_method = cd.preferred_contact_method;
    if (cd.property_type) clientIntel.property_type = cd.property_type;
    if (cd.notes) clientIntel.notes = cd.notes;
  }
  
  // Build client_discovery with budget from lead
  const clientDiscovery = {
    problem_description: capture.client_discovery?.problem || '',
    why_now: capture.client_discovery?.why_now || '',
    success_definition: capture.client_discovery?.success_definition || '',
    budget_range: capture.client_discovery?.budget_range || '',
    budget_min: null,
    budget_max: null,
    budget_flexibility: capture.client_discovery?.budget_flexibility || '',
    timeline_desired_start: capture.client_discovery?.timeline_desired_start || '',
    timeline_urgency: capture.client_discovery?.timeline_urgency || '',
    timeline_hard_deadline: capture.client_discovery?.timeline_hard_deadline || '',
    timeline_deadline_reason: capture.client_discovery?.timeline_deadline_reason || '',
    staying_during_work: capture.client_discovery?.staying_during_work || '',
    alternative_arrangements: capture.client_discovery?.alternative_arrangements || '',
    occupants_adults: capture.client_discovery?.occupants_adults || '',
    occupants_children: capture.client_discovery?.occupants_children || '',
    occupants_pets: capture.client_discovery?.occupants_pets || '',
    occupants_special_needs: capture.client_discovery?.occupants_special_needs || '',
    notes: '',
  };
  
  // Populate from lead data
  if (leadData) {
    if (!clientDiscovery.problem_description) clientDiscovery.problem_description = leadData.description || '';
    clientDiscovery.budget_min = leadData.estimated_budget_min;
    clientDiscovery.budget_max = leadData.estimated_budget_max;
    if (leadData.desired_start_date) clientDiscovery.timeline_desired_start = leadData.desired_start_date;
    if (leadData.urgency) clientDiscovery.timeline_urgency = leadData.urgency;
  }
  
  return {
    field_visit_id: fvData.field_visit_id,
    appointment_id: fvData.appointment_id || appointmentId,
    lead_id: fvData.lead_id || leadData?.lead_id || null,
    company_id: fvData.company_id || null,
    created_by_user_id: fvData.created_by_user_id,
    
    status: fvData.status || 'draft',
    current_step: fvData.step_state || 1,
    locked_at: fvData.locked_at,
    
    client_intel: clientIntel,
    
    client_discovery: clientDiscovery,
    
    decision_drivers: {
      priority: capture.decision_drivers?.priority || '',
      priority_explanation: capture.decision_drivers?.priority_explanation || '',
      biggest_concern: capture.decision_drivers?.biggest_concern || '',
      concern_details: capture.decision_drivers?.concern_details || '',
      deal_breakers: capture.decision_drivers?.deal_breakers || [],
      must_haves: capture.decision_drivers?.must_haves || [],
      nice_to_haves: capture.decision_drivers?.nice_to_haves || [],
      decision_timeline: capture.decision_drivers?.decision_timeline || '',
      decision_process: capture.decision_drivers?.decision_process || '',
      previous_contractor_experience: capture.decision_drivers?.previous_contractor_experience || '',
      notes: '',
    },
    
    measurements: {
      areas: capture.areas || [],
      total_sqft: capture.total_sqft || 0,
      lidar_scan: capture.lidar_scan || null,
      hover_report: capture.hover_report || null,
    },
    
    photos: {
      items: capture.photos || [],
    },
    
    product_selections: {
      selections: [],
      total_estimated_cost: 0,
    },
    
    risks: {
      items: capture.risks || [],
    },
    
    scope_description: {
      narrative: '',
      areas_included: [],
      special_instructions: '',
      ai_generated: false,
    },
    
    review: {
      checklist: {},
      estimator_notes: '',
      completed_at: null,
    },
    
    estimate_id: fvData.estimate_id || null,
    estimate_generated_at: fvData.estimate_generated_at || null,
    
    created_at: fvData.created_at,
    updated_at: fvData.updated_at,
  };
};

// Initial empty field visit structure
const getEmptyFieldVisit = (appointmentId) => ({
  field_visit_id: null,
  appointment_id: appointmentId,
  company_id: null,
  created_by_user_id: null,
  
  status: 'draft',
  current_step: 1,
  locked_at: null,
  
  // STEP 1: Client Intel
  client_intel: {
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    preferred_contact_method: '',
    best_time_to_reach: '',
    decision_makers: { primary: '', secondary: '', notes: '' },
    spouse_involved: '',
    referral_source: '',
    referral_details: '',
    other_quotes: '',
    other_quotes_details: { count: '', competitors: '' },
    property_type: '',
    property_ownership: '',
    property_age: '',
    property_sqft: '',
    hoa_present: '',
    hoa_restrictions: '',
    notes: '',
  },
  
  // STEP 2: Client Discovery
  client_discovery: {
    problem_description: '',
    why_now: '',
    success_definition: '',
    budget_range: '',
    budget_flexibility: '',
    timeline_desired_start: '',
    timeline_urgency: '',
    timeline_hard_deadline: '',
    timeline_deadline_reason: '',
    staying_during_work: '',
    alternative_arrangements: '',
    occupants_adults: '',
    occupants_children: '',
    occupants_children_ages: '',
    pets: '',
    pets_details: '',
    work_from_home: '',
    notes: '',
  },
  
  // STEP 3: Decision Drivers
  decision_drivers: {
    priority: '',
    priority_explanation: '',
    biggest_concern: '',
    biggest_concern_explanation: '',
    must_haves: [],
    nice_to_haves: [],
    deal_breakers: [],
    notes: '',
  },
  
  // STEP 4: Measurements
  measurements: {
    capture_method: 'manual',
    lidar_file_id: null,
    lidar_processed: false,
    areas: [],
  },
  
  // STEP 5: Photos
  photos: {
    items: [],
  },
  
  // STEP 6: Product Selection
  product_selections: {
    selections: [],
  },
  
  // STEP 7: Risk Ledger
  risks: {
    items: [],
  },
  
  // STEP 8: Scope Description
  scope_description: {
    narrative: '',
    areas_included: [],
    special_instructions: '',
    ai_generated: false,
  },
  
  // STEP 9: Review
  review: {
    checklist: {
      client_intel_complete: false,
      discovery_complete: false,
      drivers_complete: false,
      measurements_complete: false,
      photos_complete: false,
      products_selected: false,
      risks_documented: false,
      scope_written: false,
    },
    estimator_notes: '',
    completed_at: null,
  },
  
  estimate_id: null,
  estimate_generated_at: null,
  
  created_at: null,
  updated_at: null,
});

export const useFieldVisit = (appointmentId, sessionToken) => {
  const [fieldVisit, setFieldVisit] = useState(null);
  const [context, setContext] = useState(null); // Appointment & lead info
  const [settings, setSettings] = useState(DEFAULT_FIELD_VISIT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [completedSteps, setCompletedSteps] = useState([]);

  // Load or start field visit
  useEffect(() => {
    if (!appointmentId || !sessionToken) return;
    
    const loadFieldVisit = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Try to start/resume field visit
        const response = await fetch(
          `${API_BASE}/api/field-visits/appointments/${appointmentId}/field-visit/start`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${sessionToken}`,
              'Content-Type': 'application/json',
            },
          }
        );
        
        if (!response.ok) {
          throw new Error('Failed to load field visit');
        }
        
        const rawData = await response.json();
        
        // The API returns { field_visit, appointment, lead }
        const fvData = rawData.field_visit || rawData;
        const leadData = rawData.lead || null;
        const appointmentData = rawData.appointment || null;
        
        // Map backend structure to frontend structure
        // Pass lead and appointment data for pre-population
        const mappedData = mapBackendToFrontend(fvData, appointmentId, leadData, appointmentData);
        
        setFieldVisit(mappedData);
        setContext({
          appointment: appointmentData,
          lead: leadData,
        });
        
        // Calculate completed steps
        updateCompletedSteps(mappedData);
        
      } catch (err) {
        console.error('Error loading field visit:', err);
        setError(err.message);
        // Initialize with empty structure on error
        setFieldVisit(getEmptyFieldVisit(appointmentId));
      } finally {
        setLoading(false);
      }
    };
    
    loadFieldVisit();
  }, [appointmentId, sessionToken]);

  // Update completed steps based on field visit data
  const updateCompletedSteps = useCallback((fv) => {
    if (!fv) return;
    
    const completed = [];
    
    // Step 1: Client Intel
    if (fv.client_intel?.contact_name && fv.client_intel?.contact_phone && fv.client_intel?.property_type) {
      completed.push(1);
    }
    
    // Step 2: Client Discovery
    if (fv.client_discovery?.problem_description && fv.client_discovery?.why_now && fv.client_discovery?.success_definition) {
      completed.push(2);
    }
    
    // Step 3: Decision Drivers
    if (fv.decision_drivers?.priority && fv.decision_drivers?.biggest_concern) {
      completed.push(3);
    }
    
    // Step 4: Measurements
    if (fv.measurements?.areas?.length > 0) {
      completed.push(4);
    }
    
    // Step 5: Photos
    if (fv.photos?.items?.length > 0) {
      completed.push(5);
    }
    
    // Step 6: Product Selection
    if (fv.product_selections?.selections?.length > 0 || !hasReplacements(fv)) {
      completed.push(6);
    }
    
    // Step 7: Risks
    if (fv.risks?.items?.length > 0) {
      completed.push(7);
    }
    
    // Step 8: Scope
    if (fv.scope_description?.narrative?.length >= 100) {
      completed.push(8);
    }
    
    setCompletedSteps(completed);
  }, []);

  // Check if there are any replacements that need product selection
  const hasReplacements = (fv) => {
    if (!fv?.measurements?.areas) return false;
    return fv.measurements.areas.some(area => 
      area.replacements && area.replacements.length > 0
    );
  };

  // Save field visit to server
  const saveFieldVisit = useCallback(async () => {
    if (!fieldVisit?.field_visit_id || !sessionToken) return;
    
    setSaving(true);
    setError(null);
    
    try {
      const response = await fetch(
        `${API_BASE}/api/field-visits/${fieldVisit.field_visit_id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(fieldVisit),
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to save field visit');
      }
      
      const data = await response.json();
      setFieldVisit(data);
      updateCompletedSteps(data);
      
      return data;
    } catch (err) {
      console.error('Error saving field visit:', err);
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [fieldVisit, sessionToken, updateCompletedSteps]);

  // Update a specific step's data
  const updateStep = useCallback((stepKey, data) => {
    setFieldVisit(prev => {
      if (!prev) return prev;
      const updated = {
        ...prev,
        [stepKey]: {
          ...(prev[stepKey] || {}),
          ...data,
        },
        updated_at: new Date().toISOString(),
      };
      // Update completed steps after change
      setTimeout(() => updateCompletedSteps(updated), 0);
      return updated;
    });
  }, [updateCompletedSteps]);

  // Update nested field
  const updateField = useCallback((stepKey, fieldPath, value) => {
    setFieldVisit(prev => {
      if (!prev) return prev;
      
      const updated = { ...prev };
      const keys = fieldPath.split('.');
      let current = updated[stepKey] = { ...(updated[stepKey] || {}) };
      
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...(current[keys[i]] || {}) };
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      updated.updated_at = new Date().toISOString();
      
      // Update completed steps after change
      setTimeout(() => updateCompletedSteps(updated), 0);
      return updated;
    });
  }, [updateCompletedSteps]);

  // Add area
  const addArea = useCallback((areaType, label) => {
    const newArea = {
      area_id: `area_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: areaType,
      label: label || areaType,
      dimensions: {
        length_ft: null,
        length_in: null,
        width_ft: null,
        width_in: null,
        ceiling_height_ft: null,
        ceiling_height_in: null,
        floor_sf: null,
        wall_sf: null,
        perimeter_lf: null,
      },
      fixtures: {},
      replacements: [],
      notes: '',
    };
    
    setFieldVisit(prev => {
      if (!prev) return prev;
      const areas = [...(prev.measurements?.areas || []), newArea];
      return {
        ...prev,
        measurements: {
          ...(prev.measurements || {}),
          areas,
        },
        updated_at: new Date().toISOString(),
      };
    });
    
    return newArea;
  }, []);

  // Remove area
  const removeArea = useCallback((areaId) => {
    setFieldVisit(prev => {
      if (!prev) return prev;
      const areas = (prev.measurements?.areas || []).filter(a => a.area_id !== areaId);
      return {
        ...prev,
        measurements: {
          ...(prev.measurements || {}),
          areas,
        },
        updated_at: new Date().toISOString(),
      };
    });
  }, []);

  // Update area
  const updateArea = useCallback((areaId, data) => {
    setFieldVisit(prev => {
      if (!prev) return prev;
      const areas = (prev.measurements?.areas || []).map(a => 
        a.area_id === areaId ? { ...a, ...data } : a
      );
      return {
        ...prev,
        measurements: {
          ...(prev.measurements || {}),
          areas,
        },
        updated_at: new Date().toISOString(),
      };
    });
  }, []);

  // Add photo
  const addPhoto = useCallback((photoData) => {
    const newPhoto = {
      photo_id: `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      ...photoData,
      created_at: new Date().toISOString(),
    };
    
    setFieldVisit(prev => {
      if (!prev) return prev;
      const items = [...(prev.photos?.items || []), newPhoto];
      return {
        ...prev,
        photos: {
          ...(prev.photos || {}),
          items,
        },
        updated_at: new Date().toISOString(),
      };
    });
    
    return newPhoto;
  }, []);

  // Remove photo
  const removePhoto = useCallback((photoId) => {
    setFieldVisit(prev => {
      if (!prev) return prev;
      const items = (prev.photos?.items || []).filter(p => p.photo_id !== photoId);
      return {
        ...prev,
        photos: {
          ...(prev.photos || {}),
          items,
        },
        updated_at: new Date().toISOString(),
      };
    });
  }, []);

  // Add risk
  const addRisk = useCallback((riskData) => {
    const newRisk = {
      risk_id: `risk_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      ...riskData,
      created_at: new Date().toISOString(),
    };
    
    setFieldVisit(prev => {
      if (!prev) return prev;
      const items = [...(prev.risks?.items || []), newRisk];
      return {
        ...prev,
        risks: {
          ...(prev.risks || {}),
          items,
        },
        updated_at: new Date().toISOString(),
      };
    });
    
    return newRisk;
  }, []);

  // Remove risk
  const removeRisk = useCallback((riskId) => {
    setFieldVisit(prev => {
      if (!prev) return prev;
      const items = (prev.risks?.items || []).filter(r => r.risk_id !== riskId);
      return {
        ...prev,
        risks: {
          ...(prev.risks || {}),
          items,
        },
        updated_at: new Date().toISOString(),
      };
    });
  }, []);

  // Validate step
  const validateStep = useCallback((step) => {
    const requiredFields = getRequiredFieldsForStep(step, settings);
    const errors = [];
    
    const getStepData = () => {
      switch (step) {
        case 1: return fieldVisit?.client_intel || {};
        case 2: return fieldVisit?.client_discovery || {};
        case 3: return fieldVisit?.decision_drivers || {};
        case 4: return fieldVisit?.measurements || {};
        case 5: return fieldVisit?.photos || {};
        case 6: return fieldVisit?.product_selections || {};
        case 7: return fieldVisit?.risks || {};
        case 8: return fieldVisit?.scope_description || {};
        case 9: return fieldVisit?.review || {};
        default: return {};
      }
    };
    
    const stepData = getStepData();
    
    requiredFields.forEach(field => {
      const value = stepData[field.key];
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        errors.push(field.label);
      }
    });
    
    // Special validations per step
    if (step === 4 && (!fieldVisit?.measurements?.areas || fieldVisit.measurements.areas.length < settings.step4_settings?.min_areas_required)) {
      errors.push('At least one area required');
    }
    
    if (step === 5 && (!fieldVisit?.photos?.items || fieldVisit.photos.items.length < settings.step5_settings?.min_photos_required)) {
      errors.push('At least one photo required');
    }
    
    if (step === 7 && (!fieldVisit?.risks?.items || fieldVisit.risks.items.length < settings.step7_settings?.min_risks_required)) {
      errors.push('At least one risk entry required');
    }
    
    if (step === 8 && settings.step8_settings?.scope_narrative_required) {
      const narrative = fieldVisit?.scope_description?.narrative || '';
      if (narrative.length < (settings.step8_settings?.min_characters || 100)) {
        errors.push(`Scope narrative must be at least ${settings.step8_settings?.min_characters || 100} characters`);
      }
    }
    
    return errors;
  }, [fieldVisit, settings]);

  // Complete field visit
  const completeFieldVisit = useCallback(async () => {
    if (!fieldVisit?.field_visit_id || !sessionToken) return;
    
    setSaving(true);
    setError(null);
    
    try {
      // First save current state
      await saveFieldVisit();
      
      // Then complete
      const response = await fetch(
        `${API_BASE}/api/field-visits/${fieldVisit.field_visit_id}/complete`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to complete field visit');
      }
      
      const data = await response.json();
      setFieldVisit(data);
      
      return data;
    } catch (err) {
      console.error('Error completing field visit:', err);
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [fieldVisit, sessionToken, saveFieldVisit]);

  // Generate estimate
  const generateEstimate = useCallback(async () => {
    if (!fieldVisit?.field_visit_id || !sessionToken) return;
    
    setSaving(true);
    setError(null);
    
    try {
      const response = await fetch(
        `${API_BASE}/api/field-visits/${fieldVisit.field_visit_id}/generate-estimate`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to generate estimate');
      }
      
      const data = await response.json();
      setFieldVisit(prev => ({
        ...prev,
        estimate_id: data.estimate_id,
        estimate_generated_at: new Date().toISOString(),
      }));
      
      return data;
    } catch (err) {
      console.error('Error generating estimate:', err);
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [fieldVisit, sessionToken]);

  return {
    // State
    fieldVisit,
    context,
    settings,
    loading,
    saving,
    error,
    completedSteps,
    isComplete: fieldVisit?.status === 'complete',
    
    // Actions
    setFieldVisit,
    saveFieldVisit,
    updateStep,
    updateField,
    validateStep,
    completeFieldVisit,
    generateEstimate,
    
    // Area helpers
    addArea,
    removeArea,
    updateArea,
    
    // Photo helpers
    addPhoto,
    removePhoto,
    
    // Risk helpers
    addRisk,
    removeRisk,
  };
};

export default useFieldVisit;
